import * as z from 'zod';
import { SideSchema } from './game-state';
import { HordeReferenceValueSchema, IdSchema } from './horde-schema-primitives';
import { CorePhaseSchema } from './horde-session-core';

/** The table-side reason a Horde unit left play. */
export const HordeUnitDestructionCauseSchema = z.enum([
    'playerAttack',
    'playerAbility',
    'playerAction',
    'spawnZone',
    'other',
]);
export type HordeUnitDestructionCause = z.infer<typeof HordeUnitDestructionCauseSchema>;

/** Public attribution retained when a Horde unit is destroyed. */
export const HordeUnitDestructionSchema = z.object({
    battleRound: z.number().int().positive().nullable(),
    cause: HordeUnitDestructionCauseSchema,
    responsiblePlayerId: IdSchema.nullable(),
});
export type HordeUnitDestruction = z.infer<typeof HordeUnitDestructionSchema>;

/** Runtime-only current-content projection for a persisted Horde datasheet id. */
export const HordeUnitCapabilitiesSchema = z.object({
    hasDeepStrike: z.boolean(),
    hasScouts: z.boolean(),
    isCharacter: z.boolean(),
    label: z.string().min(1),
});
export type HordeUnitCapabilities = z.infer<typeof HordeUnitCapabilitiesSchema>;

export const HordeSessionUnitSchema = z
    .object({
        battleShocked: z.boolean(),
        datasheetId: IdSchema,
        destruction: HordeUnitDestructionSchema.nullable().default(null),
        factionId: IdSchema,
        id: IdSchema,
        ingressMovedThisRound: z.boolean(),
        originalBracket: IdSchema,
        originalBracketMinimum: z.number().int(),
        phaseProgress: z.partialRecord(CorePhaseSchema, z.enum(['pending', 'resolved', 'ineligible'])),
        spawnZoneId: IdSchema.nullable(),
        spawnedRound: z.number().int().positive(),
        spawnedThisRound: z.boolean(),
        status: z.enum(['battlefield', 'strategicReserves', 'setAside', 'destroyed']),
        transportState: z.enum(['none', 'transport', 'embarked']),
        treatedAsChargedThisTurn: z.boolean(),
    })
    .strict();
export type HordeSessionUnit = z.infer<typeof HordeSessionUnitSchema>;

export const DeferredIngressSchema = z.object({
    createdRound: z.number().int().positive(),
    policy: z.enum(['standard', 'pincerManeuver']),
    resolved: z.boolean(),
    unitId: IdSchema,
});
export type DeferredIngress = z.infer<typeof DeferredIngressSchema>;

export const SetAsideUnitSchema = z.object({
    availableRound: z.number().int().positive(),
    originalUnitId: IdSchema,
    respawnedUnitId: IdSchema.nullable(),
    status: z.enum(['waiting', 'respawned', 'cancelled']),
});
export type SetAsideUnit = z.infer<typeof SetAsideUnitSchema>;

export const HordeStateSchema = z
    .object({
        currentActivationUnitId: IdSchema.nullable(),
        /** Durable reference to a replicated custom bracket. Current content hydrates it at the edge. */
        customSpawnBracketId: IdSchema.nullable().default(null),
        deferredIngress: z.array(DeferredIngressSchema),
        factionIds: z.array(IdSchema),
        primaryFactionId: IdSchema.nullable(),
        primaryFactionRuleId: IdSchema.nullable(),
        setAsideForRespawn: z.array(SetAsideUnitSchema),
        units: z.array(HordeSessionUnitSchema),
        warlordUnitId: IdSchema.nullable(),
    })
    .strict();
export type HordeSessionState = z.infer<typeof HordeStateSchema>;

export const RecordedTableFactSchema = z
    .object({
        expiresAt: z
            .object({
                phase: CorePhaseSchema.optional(),
                round: z.number().int().positive().optional(),
                side: SideSchema.optional(),
            })
            .strict()
            .nullable(),
        id: IdSchema,
        kind: z.string().min(1).max(128),
        recordedAtRevision: z.number().int().nonnegative(),
        subjectRefs: z.array(IdSchema),
        value: z.union([
            HordeReferenceValueSchema,
            z.string().min(1).max(512),
            z
                .object({
                    amount: z.number().optional(),
                    confirmed: z.boolean().optional(),
                    controlledObjectiveCount: z.number().int().nonnegative().optional(),
                    engagementRange: z.boolean().optional(),
                    overflowedIntoNoMansLand: z.boolean().optional(),
                    responsiblePlayerId: IdSchema.nullable().optional(),
                    soloDoublingApplied: z.boolean().optional(),
                    sourceId: IdSchema.optional(),
                })
                .strict(),
            z
                .object({
                    hordeUnitIds: z.array(IdSchema),
                    playerUnitRefs: z.array(IdSchema),
                })
                .strict(),
            z.array(
                z
                    .object({
                        bracketMinimum: z.number().int(),
                        factionId: IdSchema,
                        hordeUnitId: IdSchema.optional(),
                        playerUnitRef: IdSchema.optional(),
                    })
                    .strict()
            ),
        ]),
    })
    .strict();
export type RecordedTableFact = z.infer<typeof RecordedTableFactSchema>;

export const FortifiedMarkerSchema = z.object({
    createdByPlayerId: IdSchema,
    createdRound: z.number().int().positive(),
    markerTypeId: IdSchema,
    uniqueName: z.string().min(1),
});
export type FortifiedMarker = z.infer<typeof FortifiedMarkerSchema>;

export const ObjectiveStateSchema = z.object({
    area: z.enum(['attackerDeployment', 'noMansLand', 'defenderDeployment']),
    controlledBy: z.enum(['horde', 'players', 'neither', 'contested', 'unknown']),
    controllerPlayerIds: z.array(IdSchema),
    exists: z.boolean(),
    fortifiedMarker: FortifiedMarkerSchema.nullable(),
    id: IdSchema,
    kind: z.enum(['marker', 'terrain']),
});
export type HordeObjectiveState = z.infer<typeof ObjectiveStateSchema>;

export const SpawnZoneStateSchema = z.object({
    bordersNoMansLandConfirmed: z.boolean(),
    id: IdSchema,
    label: z.string().min(1),
});
export type HordeSpawnZoneState = z.infer<typeof SpawnZoneStateSchema>;

export const BattlefieldMarkerSchema = z.object({
    createdByPlayerId: IdSchema.nullable(),
    id: IdSchema,
    kind: z.string().min(1),
    status: z.enum(['active', 'destroyed', 'removed']),
    tally: z.number(),
    uniqueName: z.string().nullable(),
});
export type BattlefieldMarker = z.infer<typeof BattlefieldMarkerSchema>;

export const BattlefieldStateSchema = z
    .object({
        attacker: z.literal('horde'),
        defender: z.literal('players'),
        deploymentId: IdSchema.nullable(),
        destroyedObjectiveIds: z.array(IdSchema),
        markers: z.array(BattlefieldMarkerSchema),
        objectives: z.array(ObjectiveStateSchema),
        removedTerrainIds: z.array(IdSchema),
        spawnZones: z.array(SpawnZoneStateSchema),
        tableFacts: z.array(RecordedTableFactSchema),
    })
    .strict();
export type HordeBattlefieldState = z.infer<typeof BattlefieldStateSchema>;
